import { API_BASE } from '../../lib/apiOrigin';
import { useAdminLanguage } from "../../i18n/adminTranslations";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw, Plus, Trash2, Upload, Edit3, Eye, EyeOff, GripVertical, X, Save } from "lucide-react";

const API = API_BASE;

// Categories are free-form on the backend; these are just quick-pick chips.
const CATEGORY_OPTIONS = ["Award", "Certification", "Recognition", "License", "Insurance"];

const emptyDraft = () => ({
  title: "",
  description: "",
  year: "",
  category: "Award",
  image_url: "",
  display_order: 0,
  is_active: true,
});

/**
 * Full CRUD dashboard for dynamic Awards / Certifications / Recognitions
 * (backend: /api/admin/awards). Different from the legacy "Awards" tab
 * which manages the fixed Nextdoor Fave 3-year image slots.
 */
const RecognitionsTab = ({ token }) => {
  const { a } = useAdminLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null); // id or "new"
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/awards`, {
        headers: { "X-Admin-Token": token },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load recognitions.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setDraft({ ...emptyDraft(), display_order: items.length });
    setEditingId("new");
  };

  const startEdit = (item) => {
    setDraft({ ...emptyDraft(), ...item });
    setEditingId(item.id);
  };

  const cancelEdit = () => {
    setDraft(emptyDraft());
    setEditingId(null);
  };

  const save = async () => {
    if (!draft.title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingId === "new") {
        const res = await axios.post(`${API}/admin/awards`, draft, {
          headers: { "X-Admin-Token": token },
        });
        setItems((prev) => [...prev, res.data]);
      } else {
        const res = await axios.put(`${API}/admin/awards/${editingId}`, draft, {
          headers: { "X-Admin-Token": token },
        });
        setItems((prev) => prev.map((i) => (i.id === editingId ? res.data : i)));
      }
      cancelEdit();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this recognition? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/awards/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await axios.put(
        `${API}/admin/awards/${item.id}`,
        { is_active: !item.is_active },
        { headers: { "X-Admin-Token": token } }
      );
      setItems((prev) => prev.map((i) => (i.id === item.id ? res.data : i)));
    } catch (e) {
      setError("Failed to toggle visibility.");
    }
  };

  const move = async (item, direction) => {
    const idx = items.findIndex((i) => i.id === item.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const other = items[swapIdx];
    const optimistic = [...items];
    optimistic[idx] = { ...other, display_order: item.display_order };
    optimistic[swapIdx] = { ...item, display_order: other.display_order };
    setItems(optimistic);
    try {
      await Promise.all([
        axios.put(
          `${API}/admin/awards/${item.id}`,
          { display_order: other.display_order },
          { headers: { "X-Admin-Token": token } }
        ),
        axios.put(
          `${API}/admin/awards/${other.id}`,
          { display_order: item.display_order },
          { headers: { "X-Admin-Token": token } }
        ),
      ]);
    } catch (e) {
      setError("Failed to reorder. Refresh to re-sync.");
      load();
    }
  };

  return (
    <div data-testid="recognitions-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl mb-2">{a("Recognitions")}</h2>
          <p className="text-zinc-300 text-base max-w-2xl leading-relaxed">
            Awards, certifications, licenses and insurance credentials displayed on the public site.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="recognitions-new"
            onClick={startNew}
            className="inline-flex items-center gap-2 border border-sky-400 text-sky-300 hover:bg-sky-500/10 px-5 py-3 text-base font-bold uppercase tracking-[0.12em]"
          >
            <Plus size={18} /> {a("New")}
          </button>
          <button
            data-testid="recognitions-refresh"
            onClick={load}
            className="h-11 w-11 border border-white/15 text-white hover:border-sky-400 flex items-center justify-center"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="recognitions-error" className="text-red-300 text-base mb-5 bg-red-500/10 border border-red-500/30 px-4 py-3">
          {a(error)}
        </div>
      )}

      {editingId && (
        <RecognitionForm
          draft={draft}
          setDraft={setDraft}
          onCancel={cancelEdit}
          onSave={save}
          saving={saving}
          isNew={editingId === "new"}
        />
      )}

      <div className="space-y-4 mt-6">
        {items.length === 0 && !loading ? (
          <div className="border border-white/10 p-10 text-center text-zinc-300">
            No recognitions yet. Click <span className="text-sky-300 font-semibold">+ New</span> to add one.
          </div>
        ) : (
          items.map((item, idx) => (
            <RecognitionCard
              key={item.id}
              item={item}
              onEdit={() => startEdit(item)}
              onDelete={() => remove(item.id)}
              onToggle={() => toggleActive(item)}
              onMoveUp={() => move(item, -1)}
              onMoveDown={() => move(item, +1)}
              canMoveUp={idx > 0}
              canMoveDown={idx < items.length - 1}
            />
          ))
        )}
      </div>
    </div>
  );
};

const RecognitionCard = ({ item, onEdit, onDelete, onToggle, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) => {
  const { a } = useAdminLanguage();
  return (<div
    data-testid={`recognition-card-${item.id}`}
    className={`border p-5 sm:p-6 flex flex-col sm:flex-row gap-5 items-start ${
      item.is_active ? "border-white/15 bg-zinc-950/60" : "border-white/5 bg-zinc-950/30 opacity-60"
    }`}
  >
    <div className="flex flex-col gap-1 pt-1">
      <button
        onClick={onMoveUp}
        disabled={!canMoveUp}
        title={a("Move up")}
        className="h-7 w-7 border border-white/10 text-zinc-400 hover:text-sky-400 hover:border-sky-400 disabled:opacity-30 flex items-center justify-center"
      >
        ↑
      </button>
      <button
        onClick={onMoveDown}
        disabled={!canMoveDown}
        title={a("Move down")}
        className="h-7 w-7 border border-white/10 text-zinc-400 hover:text-sky-400 hover:border-sky-400 disabled:opacity-30 flex items-center justify-center"
      >
        ↓
      </button>
    </div>

    {item.image_url ? (
      <img
        src={item.image_url}
        alt={item.title}
        className="w-full sm:w-40 h-32 object-cover border border-white/10"
      />
    ) : (
      <div className="w-full sm:w-40 h-32 border border-dashed border-white/15 flex items-center justify-center text-zinc-600 text-xs">
        {a("No image")}
      </div>
    )}

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-sky-400">
          {item.category || "Award"}
        </span>
        {item.year && (
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
            · {item.year}
          </span>
        )}
        {!item.is_active && (
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-400">
            · Hidden
          </span>
        )}
      </div>
      <h3 className="font-display font-black text-white text-xl leading-tight mb-2">{item.title}</h3>
      {item.description && (
        <p className="text-zinc-300 text-sm leading-relaxed line-clamp-3">{item.description}</p>
      )}
    </div>

    <div className="flex sm:flex-col gap-2 shrink-0">
      <button
        data-testid={`recognition-edit-${item.id}`}
        onClick={onEdit}
        className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-sky-400 hover:text-sky-300 px-4 py-2 text-sm font-semibold"
      >
        <Edit3 size={14} /> {a("Edit")}
      </button>
      <button
        data-testid={`recognition-toggle-${item.id}`}
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-amber-400 hover:text-amber-300 px-4 py-2 text-sm font-semibold"
      >
        {item.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
        {a(item.is_active ? "Hide" : "Show")}
      </button>
      <button
        data-testid={`recognition-delete-${item.id}`}
        onClick={onDelete}
        className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-red-400 hover:text-red-300 px-4 py-2 text-sm font-semibold"
      >
        <Trash2 size={14} /> {a("Delete")}
      </button>
    </div>
  </div>);
};

const RecognitionForm = ({ draft, setDraft, onCancel, onSave, saving, isNew }) => {
  const { a } = useAdminLanguage();
  const fileRef = useRef(null);
  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setDraft((d) => ({ ...d, _error: "Image too large (max 6MB)." }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDraft((d) => ({ ...d, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div
      data-testid="recognition-form"
      className="border border-sky-400/50 bg-zinc-950 p-6 sm:p-8 space-y-5"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-black text-white text-2xl">
          {a(isNew ? "New recognition" : "Edit recognition")}
        </h3>
        <button
          data-testid="recognition-form-cancel"
          onClick={onCancel}
          className="text-zinc-400 hover:text-white p-2"
          title={a("Close")}
        >
          <X size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Title *</span>
          <input
            data-testid="recognition-input-title"
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
            placeholder="e.g. Nextdoor Neighborhood Fave 2025"
            maxLength={180}
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Year</span>
          <input
            data-testid="recognition-input-year"
            value={draft.year}
            onChange={(e) => setDraft((d) => ({ ...d, year: e.target.value }))}
            className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
            placeholder="2025"
            maxLength={20}
          />
        </label>
      </div>

      <div>
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">{a("Category")}</span>
        <div className="flex flex-wrap gap-2 mb-2">
          {CATEGORY_OPTIONS.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`recognition-category-${c.toLowerCase()}`}
              onClick={() => setDraft((d) => ({ ...d, category: c }))}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border transition-colors ${
                draft.category === c
                  ? "border-sky-400 text-sky-300 bg-sky-500/10"
                  : "border-white/15 text-zinc-300 hover:border-sky-400/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <input
          data-testid="recognition-input-category"
          value={draft.category}
          onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
          className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
          placeholder="Or type a custom category"
          maxLength={60}
        />
      </div>

      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Description</span>
        <textarea
          data-testid="recognition-input-description"
          value={draft.description || ""}
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          rows={3}
          className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none resize-none"
          placeholder="Short blurb about this recognition"
          maxLength={1200}
        />
      </label>

      <div>
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Image (optional)</span>
        <div className="flex items-start gap-4">
          {draft.image_url ? (
            <div className="relative">
              <img src={draft.image_url} alt="" className="w-40 h-32 object-cover border border-white/10" />
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, image_url: "" }))}
                className="absolute -top-2 -right-2 h-7 w-7 bg-black border border-red-400 text-red-300 flex items-center justify-center"
                title={a("Remove image")}
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="w-40 h-32 border border-dashed border-white/15 flex items-center justify-center text-zinc-600 text-xs">
              {a("No image")}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="recognition-input-file"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 border border-white/20 text-white hover:border-sky-400 px-4 py-2.5 text-sm font-semibold"
            >
              <Upload size={14} /> {a("Upload image")}
            </button>
            <p className="text-zinc-500 text-xs">Max 6MB. JPG or PNG.</p>
          </div>
        </div>
      </div>

      <label className="inline-flex items-center gap-3 select-none">
        <input
          type="checkbox"
          data-testid="recognition-input-active"
          checked={!!draft.is_active}
          onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
          className="h-5 w-5 accent-sky-500"
        />
        <span className="text-zinc-200 text-sm">{a("Show publicly on the site")}</span>
      </label>

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="border border-white/15 text-zinc-200 hover:border-white/30 px-5 py-3 text-sm font-bold uppercase tracking-[0.12em]"
        >
          {a("Cancel")}
        </button>
        <button
          type="button"
          data-testid="recognition-form-save"
          onClick={onSave}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} />
          {a(saving ? "Saving…" : "Save")}
        </button>
      </div>
    </div>
  );
};

export default RecognitionsTab;
